// components/data-quality/AbandonedBookingsChart.jsx
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from "chart.js";
import { Pie, Line } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

const AbandonedBookingsChart = ({
  abandonedCount,
  totalBookings,
  qualityTrend,
}) => {
  // Data for pie chart
  const pieData = {
    labels: ["Active Bookings", "Abandoned Bookings"],
    datasets: [
      {
        data: [totalBookings - abandonedCount, abandonedCount],
        backgroundColor: [
          "rgba(34, 197, 94, 0.8)", // Green for active
          "rgba(239, 68, 68, 0.8)", // Red for abandoned
        ],
        borderColor: ["rgba(34, 197, 94, 1)", "rgba(239, 68, 68, 1)"],
        borderWidth: 1,
      },
    ],
  };

  // Options for pie chart
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 15,
          font: {
            size: 11,
          },
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return chart.data.labels.map((label, i) => {
              const value = datasets[0].data[i];
              const percentage = ((value / totalBookings) * 100).toFixed(1);
              return {
                text: `${label} (${value}, ${percentage}%)`,
                fillStyle: datasets[0].backgroundColor[i],
                strokeStyle: datasets[0].borderColor[i],
                lineWidth: 1,
                hidden: false,
                index: i,
              };
            });
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const percentage = ((value / totalBookings) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  // Format months for better display (e.g., "2025-04" to "Apr 2025")
  const formatMonth = (monthStr) => {
    try {
      const [year, month] = monthStr.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1);
      return date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return monthStr;
    }
  };

  // Data for trend chart
  const trendData = {
    labels: qualityTrend.map((item) => formatMonth(item.month)),
    datasets: [
      {
        label: "Abandoned Rate",
        data: qualityTrend.map((item) => item.abandonedPercentage),
        borderColor: "rgba(239, 68, 68, 1)",
        backgroundColor: "rgba(239, 68, 68, 0.5)",
        tension: 0.3,
      },
    ],
  };

  // Options for trend chart
  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `Abandoned Rate: ${context.parsed.y.toFixed(1)}%`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Abandoned Rate (%)",
          font: {
            size: 11,
          },
        },
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
    },
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-red-600 mr-2"
          />
          Abandoned Bookings Analysis
        </h3>
        <div className="text-sm text-red-600 font-medium">
          {abandonedCount} abandoned (
          {((abandonedCount / totalBookings) * 100).toFixed(1)}%)
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 flex-grow">
        <div className="p-1">
          <div className="flex justify-center h-full w-full">
            <Pie data={pieData} options={pieOptions} />
          </div>
        </div>
        <div className="p-1">
          <div className="h-full w-full">
            <Line data={trendData} options={trendOptions} />
          </div>
        </div>
      </div>

      <div className="mt-2 text-xs text-gray-500">
        <p>
          Bookings are considered abandoned if they have not been updated in
          more than 14 days and do not have a "Delivered" status.
        </p>
      </div>
    </div>
  );
};

export default AbandonedBookingsChart;
